/**
 * Modern REST API Client
 * Replaces Parse Cloud functions with modern REST API calls
 */

// Get API base URL from environment or default to api.heresmyinfo.com on production
const rawApiBase = window.HMI_API_BASE || process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location && window.location.hostname.includes('heresmyinfo.com') ? 'https://api.heresmyinfo.com' : (typeof window !== 'undefined' ? window.location.origin : 'https://api.heresmyinfo.com'));
const API_BASE_URL = rawApiBase.replace(/\/api\/?$/, '');

// Central auth token getter: prefer JWT, fallback to Parse sessionToken
function getAuthToken() {
    try {
        const jwt = window.localStorage.getItem('HMI_AUTH_TOKEN');
        if (jwt && jwt !== 'No token') return jwt;
    } catch (_) { }
    try {
        const currentUser = localStorage.getItem(`Parse/${process.env.REACT_APP_PARSE_APP_ID}/currentUser`);
        if (currentUser) {
            const userData = JSON.parse(currentUser);
            return userData.sessionToken || null;
        }
    } catch (_) { }
    return null;
}

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Fetch options
 * @returns {Promise<any>} API response
 */
async function apiRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    const token = getAuthToken();

    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers
        }
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
            // Clear token and redirect to login
            localStorage.removeItem('HMI_AUTH_TOKEN');
            
            // Also remove Parse currentUser to prevent 401 loops for legacy users
            try {
                if (process.env.REACT_APP_PARSE_APP_ID) {
                    localStorage.removeItem(`Parse/${process.env.REACT_APP_PARSE_APP_ID}/currentUser`);
                }
            } catch (e) {}

            // Only redirect if we're not already on login page
            if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
                window.location.href = '/login'
            }
            const errorData = await response.json().catch(() => ({ message: 'Token expired' }));
            throw new Error(errorData.error?.message || errorData.message || 'Token expired');
        }
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
}

/**
 * Modern API functions to replace Parse Cloud functions
 */
export const ModernAPI = {
    // Expose for callers using this.getToken()
    getToken() { return getAuthToken(); },
    // User and Profile functions
    async getSingleUser(userId) {
        return apiRequest('/api/users/profile');
    },

    async getUserWithProperties(userId) {
        return apiRequest('/api/users/profile');
    },

    async updateProfile(profileData) {
        return apiRequest('/api/users/profile', {
            method: 'PUT',
            body: JSON.stringify(profileData)
        });
    },

    async getMergedContacts() {
        return apiRequest('/api/users/profile/merged-contacts');
    },

    async addMergedContact(mergeRecord) {
        return apiRequest('/api/users/profile/merged-contacts', {
            method: 'POST',
            body: JSON.stringify({ mergeRecord })
        });
    },

    async addBasicPropsToProfile(phone) {
        return apiRequest('/api/users/profile/properties', {
            method: 'POST',
            body: JSON.stringify({
                name: 'phoneNumbers',
                value: phone,
                verified: false
            })
        });
    },

    // SMS Verification functions
    async sendVerifyBySMS({ phone, countryCode }) {
        // TODO: Implement SMS verification endpoint
        console.warn('SMS verification not yet implemented');
        return { success: true };
    },

    async verifyInitialSMSCode({ phone, code }) {
        // TODO: Implement SMS code verification endpoint
        console.warn('SMS code verification not yet implemented');
        return { success: true };
    },

    // Card functions
    async getCards(userId) {
        const response = await apiRequest('/api/users/cards');
        return response.cards || [];
    },

    async deleteCard(arg) {
        const cardId = (arg && typeof arg === 'object') ? arg.cardId : arg;
        return apiRequest(`/api/cards/${cardId}`, {
            method: 'DELETE'
        });
    },

    async getCardsWithConnections() {
        // For now, return cards without connections
        const response = await apiRequest('/api/users/cards');
        return {
            cards: response.cards || [],
            connections: {}
        };
    },

    // Connection functions
    async getConnectionsForUser() {
        return apiRequest('/api/users/connections');
    },

    async getConnectionsForUsers({ users, properties }) {
        const params = new URLSearchParams();
        if (users) params.append('users', users.join(','));
        if (properties) params.append('properties', properties.join(','));

        return apiRequest(`/api/users/connections?${params}`);
    },

    // Invitation functions
    async getIncomingInvitesForUser() {
        return apiRequest('/api/invitations/incoming');
    },

    async getOutGoingInvitesForUser() {
        return apiRequest('/api/invitations/outgoing');
    },

    // Support functions
    async supportMessage({ email, message }) {
        return apiRequest('/api/support/messages', {
            method: 'POST',
            body: JSON.stringify({ email, message })
        });
    },

    // Property functions
    async setPropertyVerified({ messageId, token }) {
        return apiRequest('/api/properties/verify', {
            method: 'POST',
            body: JSON.stringify({ messageId, token })
        });
    },

    // QR Code functions
    async getQRCodes() {
        const response = await apiRequest('/api/qrcodes');
        return response.success ? response.data : (Array.isArray(response) ? response : []);
    },

    async createQRCode({ label, cardId }) {
        const response = await apiRequest('/api/qrcodes', {
            method: 'POST',
            body: JSON.stringify({ label, cardId })
        });
        return response.success ? response.data : response;
    },

    async updateQRCode({ id, name, label, cardId }) {
        const response = await apiRequest(`/api/qrcodes/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ label: label || name, name, cardId })
        });
        return response.success ? response.data : response;
    },

    async deleteQRCode({ id }) {
        const response = await apiRequest(`/api/qrcodes/${id}`, {
            method: 'DELETE'
        });
        return response.success;
    },

    async attachQRCode({ qrCode }) {
        const response = await apiRequest('/api/qrcodes/attach', {
            method: 'POST',
            body: JSON.stringify({ qrCode })
        });
        return response.success ? response.data : response;
    },

    async queryQRCode({ qrCode }) {
        const response = await apiRequest(`/api/qrcodes/query/${qrCode}`);
        return response.success ? response.data : response;
    },

    async editQRName({ id, name }) {
        return this.updateQRCode({ id, name });
    },

    async setQRCard({ cardId, QRCode }) {
        // This is for updating an existing QR code's card
        const response = await apiRequest(`/api/qrcodes/${QRCode}`, {
            method: 'PUT',
            body: JSON.stringify({ cardId })
        });
        return response.success ? [response.data] : [];
    },

    async updateQRCodeLabel(qrCodeId, label) {
        // This is for updating an existing QR code's label
        const response = await apiRequest(`/api/qrcodes/${qrCodeId}`, {
            method: 'PUT',
            body: JSON.stringify({ label })
        });
        return response.success ? response.data : response;
    },

    // Card management (unified version at line 111)

    // Business functions (placeholder implementations)
    async getBusinesses() {
        console.warn('Business endpoints not yet implemented');
        return [];
    },

    async getBusiness({ id }) {
        console.warn('Business endpoints not yet implemented');
        return { business: null, templatePath: null };
    },

    async createBusiness({ name, domain }) {
        console.warn('Business endpoints not yet implemented');
        return { business: { name, domain } };
    },

    async newBusinessProperty({ businessId, name, value, rest }) {
        console.warn('Business property endpoints not yet implemented');
        return { property: { name, value } };
    },

    async editBusinessProperty({ id, value, rest }) {
        console.warn('Business property endpoints not yet implemented');
        return { property: { id, value } };
    },

    async verifyBusinessProperty({ id, code }) {
        console.warn('Business property verification not yet implemented');
        return { property: { id }, success: true };
    },

    async dispatchBusinessPropertyVerification({ id }) {
        console.warn('Business property verification dispatch not yet implemented');
        return { property: { id } };
    },

    // Backup functions
    async getContactsBackup({ version = 'latest' } = {}) {
        try {
            const data = await apiRequest(`/api/users/contacts-backup?version=${version}`);
            if (data && (data.data || data.encryptedData)) {
                return data;
            }
            // Fallback: Check if contactsBackup is included directly in the user profile
            const profileData = await apiRequest('/api/users/profile');
            if (profileData && profileData.profile?.contactsBackup) {
                const cb = profileData.profile.contactsBackup;
                const match = (version === 'initial' && cb.initial) ? cb.initial : (cb.latest || cb.initial);
                if (match && match.encryptedData) {
                    return { success: true, data: match.encryptedData, encryptedData: match.encryptedData, versionType: match.versionType, contactCount: match.contactCount };
                }
            }
            // Fallback: Check properties array on profile
            if (profileData && profileData.profile?.properties) {
                const prop = profileData.profile.properties.find(p => p.name === 'contactsBackup');
                if (prop && prop.parameters) {
                    const enc = prop.parameters['3']?.value || prop.parameters?.encryptedData?.value || prop.parameters?.backup?.value?.encryptedData;
                    if (enc) {
                        return { success: true, data: enc, encryptedData: enc, versionType: version };
                    }
                }
            }
            return data;
        } catch (error) {
            console.error('Error fetching contacts backup:', error);
            try {
                const profileData = await apiRequest('/api/users/profile');
                if (profileData && profileData.profile?.contactsBackup) {
                    const cb = profileData.profile.contactsBackup;
                    const match = (version === 'initial' && cb.initial) ? cb.initial : (cb.latest || cb.initial);
                    if (match && match.encryptedData) {
                        return { success: true, data: match.encryptedData, encryptedData: match.encryptedData, versionType: match.versionType, contactCount: match.contactCount };
                    }
                }
            } catch (pErr) {}
            return null;
        }
    },

    // Admin functions (placeholder implementations)
    async getBusinessesWithProperties({ perPage = 50, page = 1, search = '' }) {
        try {
            const queryParams = new URLSearchParams({ perPage, page, search }).toString();
            const data = await apiRequest(`/api/admin/businesses?${queryParams}`);
            return data;
        } catch (error) {
            console.error('Error fetching admin businesses', error);
            return { businesses: [], total: 0, owners: [] };
        }
    },

    async verifyBusinessName({ objectId, verified }) {
        console.warn('Admin business verification not yet implemented');
        return { success: true };
    },

    async changeBusinessState({ objectId, state }) {
        console.warn('Admin business state change not yet implemented');
        return { success: true };
    },

    async deleteBusinesses({ businesses }) {
        console.warn('Admin business deletion not yet implemented');
        return { success: true };
    },

    async adminGetBusiness({ objectId }) {
        console.warn('Admin business get not yet implemented');
        return { business: null };
    },

    async storeBusinessLogo({ id, parseFile }) {
        console.warn('Business logo storage not yet implemented');
        return { success: true };
    },

    async getAllQRCodesWithoutUser() {
        console.log('ModernAPI.getAllQRCodesWithoutUser called')
        const result = await apiRequest('/api/admin/qrcodes')
        console.log('ModernAPI.getAllQRCodesWithoutUser result:', result)
        return result.success ? result.data : []
    },

    async generateQRCodes({ amount, label }) {
        console.warn('Admin QR code generation not yet implemented');
        return { success: true };
    },

    async deleteUsers({ users }) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
        
        // Delete users one by one (since the API only supports single user deletion)
        const results = [];
        const errors = [];
        
        for (const user of users) {
            const userId = user.objectId || user.id;
            if (!userId) {
                errors.push({ user, error: 'No user ID found' });
                continue;
            }

            try {
                const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
                }

                const result = await response.json();
                if (result.success) {
                    results.push({ userId, success: true });
                } else {
                    throw new Error(result.error?.message || 'Delete failed');
                }
            } catch (error) {
                console.error(`Error deleting user ${userId}:`, error);
                errors.push({ userId, error: error.message || error.toString() });
            }
        }

        if (errors.length > 0 && results.length === 0) {
            // All deletions failed
            throw new Error(`Failed to delete users: ${errors.map(e => e.error).join(', ')}`);
        }

        if (errors.length > 0) {
            // Some deletions failed
            console.warn('Some user deletions failed:', errors);
        }

        return { 
            success: true, 
            deleted: results.length,
            failed: errors.length,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    async deleteConnections({ connections }) {
        if (!Array.isArray(connections)) {
            connections = [connections];
        }
        const results = [];
        const errors = [];
        for (const connectionId of connections) {
            try {
                const res = await apiRequest(`/api/admin/connections/${connectionId}`, {
                    method: 'DELETE'
                });
                results.push(res);
            } catch (error) {
                console.error(`Error deleting connection ${connectionId}:`, error);
                errors.push({ connectionId, error: error.message || error.toString() });
            }
        }
        if (errors.length > 0 && results.length === 0) {
            throw new Error(`Failed to delete connections: ${errors.map(e => e.error).join(', ')}`);
        }
        return { 
            success: true, 
            deleted: results.length,
            failed: errors.length,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    async toggleUserField({ userId, field, value }) {
        console.warn('Admin user field toggle not yet implemented');
        return { success: true };
    }
};

/**
 * Error handling utility
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error) => {
    return error.message && typeof error.message === 'string'
        ? error.message
        : 'Internal server error. Please try again later.';
};
